import InhibitorsIcon from 'app/icons/lol_inhibitor.svg';
import NashorsIcon from 'app/icons/lol_nashor.svg';
import TowersIcon from 'app/icons/lol_tower.svg';
import DrakesIcon from 'app/icons/lol_drake.svg';
import Dota2KillIcon from 'app/icons/dota2_kill.svg';
import Dota2TowerIcon from 'app/icons/dota2_tower.svg';
import { mapProperties } from 'app/utils/entities/match_data';
import { sort } from 'app/utils/array';

const getScoreboardGame = (data: { games: MatchGame[] }): MatchGame | undefined => {
  const runningGame = data.games.find(game => game.status === 'running');

  if (runningGame) return runningGame;

  const lastFinishedGame = sort(data.games, game => game.position)
    .reverse()
    .find(game => game.status === 'finished');

  if (lastFinishedGame) return lastFinishedGame;

  const firstNotStartedGame = sort(data.games, game => game.position).find(
    game => game.status === 'not_started'
  );

  return firstNotStartedGame;
};

const isMatchLive = (data: { games: MatchGame[]; match_status: EventStatus }): boolean =>
  data.match_status === 'running' &&
  data.games.some(game => game.status === 'running' || game.status === 'finished');

const getNumberOfGameWon = (games: MatchGame[], opponentId: number | undefined): number =>
  games.length ? games.filter(game => game.winner_id === opponentId).length : 0;

const getGameStat = (
  game: MatchGame | undefined,
  opponentId: number | undefined
): GameOpponent | undefined =>
  (game && (game.opponents as GameOpponent[]).find(opponent => opponent.id === opponentId)) ??
  undefined;

const mapMatchStatsProperties = <T extends GameOpponent>(
  properties: StatProperties<T>[],
  opponent1: T | undefined,
  opponent2: T | undefined
) => ({
  opponent1MatchStats: mapProperties(properties, opponent1),
  opponent2MatchStats: mapProperties(properties, opponent2)
});

const getLiveStats = <T extends GameOpponent>(
  videogame: VideogameSlug,
  opponent1: T | undefined,
  opponent2: T | undefined
): {
  opponent1MatchStats: StatData<T>[];
  opponent2MatchStats: StatData<T>[];
} => {
  if (videogame === 'league-of-legends') {
    const properties = [
      { name: 'inhibitors', Icon: InhibitorsIcon },
      { name: 'nashors', Icon: NashorsIcon },
      { name: 'towers', Icon: TowersIcon },
      {
        name: 'drakes',
        Icon: DrakesIcon,
        displayFunction: (val: Array<any> | number) =>
          Array.isArray(val) ? val.length.toString() : val.toString()
      }
    ] as StatProperties<T>[];

    return mapMatchStatsProperties(properties, opponent1, opponent2);
  }
  if (videogame === 'dota-2') {
    const properties = [
      { name: 'towers', Icon: Dota2TowerIcon },
      { name: 'kills', Icon: Dota2KillIcon }
    ] as StatProperties<T>[];

    return mapMatchStatsProperties(properties, opponent1, opponent2);
  }
  return {
    opponent1MatchStats: [],
    opponent2MatchStats: []
  };
};

const getFinalDota2GoldAdvantage = (
  goldAdvantage: GoldAdvantageAtTimer[] | undefined
): [number, SideDota2] | [undefined, undefined] => {
  if (!goldAdvantage || goldAdvantage.length === 0) {
    return [undefined, undefined];
  }

  const goldAdvantageLastValue = goldAdvantage?.[goldAdvantage.length - 1].value;
  const goldAdvantageSide: SideDota2 = goldAdvantageLastValue > 0 ? 'radiant' : 'dire';
  return [goldAdvantageLastValue, goldAdvantageSide];
};

export {
  getNumberOfGameWon,
  getGameStat,
  getScoreboardGame,
  isMatchLive,
  getLiveStats,
  getFinalDota2GoldAdvantage
};
